import { createBrowserRouter, Navigate } from "react-router-dom";
import { AdminLayout } from "../layouts/AdminLayout";
import { DashboardPage } from "../modules/dashboard/DashboardPage";
import { SitePage } from "../modules/site/SitePage";
import { BlogPage } from "../modules/blog/BlogPage";
import { AiBlogPage } from "../modules/aiBlog/AiBlogPage";
import { ChatbotPage } from "../modules/chatbot/ChatbotPage";
// Stage Chatbot-1 Phase 1:真实 /chatbot 页面(走 /api/chatbot/settings)。
import { ChatbotSettingsPage } from "../modules/chatbot/ChatbotSettingsPage";
import { AnalyticsPage } from "../modules/analytics/AnalyticsPage";
import { GoogleBusinessPage } from "../modules/googleBusiness/GoogleBusinessPage";
import { MailPage } from "../modules/mail/MailPage";
import { MailV2Page } from "../modules/mail-v2/MailV2Page";
import { MailV2ErrorBoundary } from "../components/ErrorBoundary";
import { MediaPage } from "../modules/media/MediaPage";
import { MediaDemoPage } from "../modules/media/MediaDemoPage";
// Stage IG-P0:Instagram 管理モジュール(6 ページ)
import { InstagramDashboardPage } from "../modules/instagram/InstagramDashboardPage";
import { InstagramInboxPage } from "../modules/instagram/InstagramInboxPage";
import { InstagramCommentsPage } from "../modules/instagram/InstagramCommentsPage";
import { InstagramContentPage } from "../modules/instagram/InstagramContentPage";
import { InstagramV2Page } from "../modules/instagram-v2/InstagramV2Page";
import { UsersPage } from "../modules/users/UsersPage";
import { SettingsPage } from "../modules/settings/SettingsPage";
import { MobileLayout } from "../mobile/layout/MobileLayout";
import { MobileDashboardPage } from "../mobile/pages/MobileDashboardPage";
import { MobileSitePage } from "../mobile/pages/MobileSitePage";
import { MobileBlogPage } from "../mobile/pages/MobileBlogPage";
import { MobileBlogEditPage } from "../mobile/pages/MobileBlogEditPage";
import { MobileAiBlogPage } from "../mobile/pages/MobileAiBlogPage";
import { MobileMailPage } from "../mobile/pages/MobileMailPage";
import { MobileMenuPage } from "../mobile/pages/MobileMenuPage";
import { MobileSettingsPage } from "../mobile/pages/MobileSettingsPage";
import { MobileAnalyticsPage } from "../mobile/pages/MobileAnalyticsPage";
import { MobileInstagramPage } from "../mobile/pages/MobileInstagramPage";
import { MobileGoogleBusinessPage } from "../mobile/pages/MobileGoogleBusinessPage";
import { MobileDetectRedirect } from "../mobile/components/MobileDetectRedirect";
import { Login } from "../pages/Login";
import { LoginVerify } from "../pages/LoginVerify";
import { Register } from "../pages/Register";
import { ProtectedRoute } from "../auth/ProtectedRoute";

// Stage RF Admin (2026-09-18):独立 router,不挂 AdminLayout / MobileLayout
import { RfAdminRouter } from "../rf-admin/Router";
// Stage RF Admin M3:公开 set-password 页面(挂在现有 app router 旁路)
import { SetPassword } from "./set-password/SetPassword";

// 招待制サインアップの feature flag。
// true でない限り /register は無条件で /login へリダイレクトする(リンクを隠しても手打ちアクセスを完全に塞ぐ)。
const ENABLE_SIGNUP = import.meta.env.VITE_ENABLE_SIGNUP === "true";

// Stage Mail-V2: Mail V2 production blueprint gate。
// false (default) の とき /mail-v2 URL を 直打ち する と /dashboard へ リダイレクト。
// V1 /mail は この flag を 一切 読 ま ない (V1 凍結)。
const MAIL_V2_ENABLED = import.meta.env.VITE_MAIL_V2_ENABLED === "true";

// 各 PC ページをモバイル UA 検出でラップするヘルパー
// ProtectedRoute は pc() の内側に置く(モバイル UA 検出 → ログイン判定の順)
const pc = (element: React.ReactNode) => (
  <MobileDetectRedirect>{element}</MobileDetectRedirect>
);

const guarded = (element: React.ReactNode) => pc(<ProtectedRoute>{element}</ProtectedRoute>);

// Stage 2C T9:/users は system_admin のみ。URL 直打ちアクセスは /dashboard にリダイレクト。
const guardedAdmin = (element: React.ReactNode) =>
  pc(
    <ProtectedRoute requireSystemAdmin>
      {element}
    </ProtectedRoute>
  );

// Stage 3C:/blog は system_admin + editor のみ。viewer は /settings にリダイレクト。
const guardedBlogEditor = (element: React.ReactNode) =>
  pc(
    <ProtectedRoute requireAnyRole={["system_admin", "editor"]} redirectTo="/settings">
      {element}
    </ProtectedRoute>
  );

// ルーティング定義
// 全ページは AdminLayout の Outlet 内にレンダリングされる
// Mobile 版は /mobile/* で別 Layout として並列配置（PC とは独立）
// PC ページは MobileDetectRedirect でラップ → iPhone/Android は自動で /mobile/* へ
// 加えて ProtectedRoute でラップ → 未ログインなら /login へ
export const router = createBrowserRouter([
  {
    path: "/",
    element: <AdminLayout />,
    children: [
      { index: true, element: guarded(<Navigate to="/dashboard" replace />) },
      { path: "dashboard", element: guarded(<DashboardPage />) },
      { path: "site", element: guarded(<SitePage />) },
      { path: "blog", element: guardedBlogEditor(<BlogPage />) },
      // Stage 4A-2:/ai-blog 是 system_admin + editor 双角色可访问,
      // viewer 重定向到 /settings(沿用 Stage 3C guardedBlogEditor 模式)。
      { path: "ai-blog", element: guardedBlogEditor(<AiBlogPage />) },
      // 2026-07-27:既存 Mock Chatbot 画面を /chatbot-demo に迁移。
      // 本物の /chatbot は Phase 1+ で実装予定、ここでは未登録(URL 直打ちすると
      // router.tsx 末尾の * → /dashboard にリダイレクトされる)。
      { path: "chatbot-demo", element: guarded(<ChatbotPage />) },
      // Stage Chatbot-1 Phase 1:真实 /chatbot 页面(system_admin + editor 可访问)。
      { path: "chatbot", element: guardedBlogEditor(<ChatbotSettingsPage />) },
      { path: "analytics", element: guarded(<AnalyticsPage />) },
      { path: "google-business", element: guarded(<GoogleBusinessPage />) },
      { path: "mail", element: guarded(<MailPage />) },
      // Mail V2 — Stage 2026-07-21: 純前端 UI Demo + VITE_MAIL_V2_REAL=true で 実 API 接続。
      // VITE_MAIL_V2_ENABLED=false (default) 時 は URL 直打ち で /dashboard へ リダイレクト。
      // V1 /mail は この flag を 一切 読 ま ない (V1 凍結)。
      {
        path: "mail-v2",
        element: MAIL_V2_ENABLED ? guarded(
          // Stage Mail-V2 Stage 4.4 (2026-07-22): 抓 Mail V2 真 stack trace 给 user 看。
          <MailV2ErrorBoundary>
            <MailV2Page />
          </MailV2ErrorBoundary>
        ) : <Navigate to="/dashboard" replace />,
      },
      { path: "media", element: guarded(<MediaPage />) },
      // 2026-07-25:旧 Mock UI 迁到 /media-demo,正式 /media 是 WP Media Library 真页。
      // /media-demo 不进 Sidebar,只能手动 URL 访问。
      { path: "media-demo", element: guarded(<MediaDemoPage />) },
      // Stage IG-P0:Instagram 管理モジュール
      // 6 ページ全て logged-in ユーザー全員アクセス可(viewer 含む)。
      // 内部の CapabilityGuard / 装飾器が役割に応じて機能表示を絞る。
      { path: "instagram", element: guarded(<Navigate to="/instagram-v2" replace />) },
      { path: "instagram-v2", element: guarded(<InstagramV2Page />) },
      { path: "instagram/inbox", element: guarded(<InstagramInboxPage />) },
      { path: "instagram/comments", element: guarded(<InstagramCommentsPage />) },
      { path: "instagram/content", element: guarded(<InstagramContentPage />) },
      { path: "instagram/analytics", element: guarded(<Navigate to="/instagram-v2" replace />) },
      { path: "instagram/settings", element: guarded(<Navigate to="/instagram-v2" replace />) },
      { path: "users", element: guardedAdmin(<UsersPage />) },
      { path: "settings", element: guarded(<SettingsPage />) },
      { path: "*", element: <Navigate to="/dashboard" replace /> },
    ],
  },
  // 認証ページは AdminLayout の外(全画面表示)
  { path: "/login", element: <Login /> },
  { path: "/login/verify", element: <LoginVerify /> },
  // /register は feature flag が true のときだけ実体を見せる。false のときは URL 直打ちでも /login へ飛ぶ。
  {
    path: "/register",
    element: ENABLE_SIGNUP ? <Register /> : <Navigate to="/login" replace />,
  },
  {
    path: "/mobile",
    element: (
      <MobileDetectRedirect>
        <ProtectedRoute>
          <MobileLayout />
        </ProtectedRoute>
      </MobileDetectRedirect>
    ),
    children: [
      { index: true, element: <Navigate to="/mobile/dashboard" replace /> },
      { path: "dashboard", element: <MobileDashboardPage /> },
      { path: "site", element: <MobileSitePage /> },
      { path: "blog", element: <MobileBlogPage /> },
      { path: "blog/edit", element: <MobileBlogEditPage /> },
      { path: "ai-blog", element: <MobileAiBlogPage /> },
      { path: "mail", element: <MobileMailPage /> },
      { path: "analytics", element: <MobileAnalyticsPage /> },
      { path: "instagram", element: <MobileInstagramPage /> },
      { path: "google-business", element: <MobileGoogleBusinessPage /> },
      { path: "menu", element: <MobileMenuPage /> },
      { path: "settings", element: <MobileSettingsPage /> },
      { path: "*", element: <Navigate to="/mobile/dashboard" replace /> },
    ],
  },
  // Stage RF Admin M3 (2026-09-18):公开 set-password 页面(挂在 app router 旁路,不挂 AdminLayout)
  { path: "/set-password", element: <SetPassword /> },
  // Stage RF Admin (2026-09-18):独立 router(/rf-admin/* 全部走这里,不挂现有 Layout)
  {
    path: "/rf-admin/*",
    element: <RfAdminRouter />,
  },
]);
